/**
 * Ta klasa zajmuje się rejestracją komponentu i komunikacją z service workerem
 */

import {NfMessage, NfMessageType} from "@/serviceWorker/workerComponents/NfMessage";
import {first, lastValueFrom, Observable, Subject} from "rxjs";
import {
    ActivateNewBookingEngineComponent,
    SetBrowserTabId,
    SetServiceInstanceId,
    ActivateNewBookingEngine,
    SetComponentId,
    GetBrowserTabId,
    RequestComponentRefresh,
    ComponentRefresh,
    ComponentRemoved,
    ServiceWorkerActivated,
    ServiceWorkerReloading
} from "@/serviceWorker/workerComponents/AllMessagesImport";
import type {ComponentTypes} from "@/serviceWorker/workerComponents/ComponentTypes";
import {Deferred} from "@/serviceWorker/workerComponents/Utilities/Deferred";
import {showPromiseError} from "@/utilities/nonDomUtils";
import {NfComponentMessage} from "@/serviceWorker/workerComponents/NfComponentMessage";
import {NfComponentGlobalMessage} from "@/serviceWorker/workerComponents/NfComponentGlobalMessage";
import {NfGlobalMessage} from "@/serviceWorker/workerComponents/NfGlobalMessage";
import {get, isNumber, isUndefined} from "lodash-es";

const iframeElementName = 'nfhotel-sw-frame';
/**
 * Odpowiada za komunikację od komponentów do service workera.
 * Podczas inicjalizacji pobiera z service workera identyfikator zakładki, instancji booking engine, komponentu.
 */
export class NfServiceWorkerCommunication {
    // @ts-ignore
    private static seviceWorkerUpdateTimeoutSeconds = import.meta.env.VITE_SERVICE_WORKER_UPDATE_INTERVAL_MINUTES *60 ;
    /**
     * Ten promise zostanie zresolvowany z kanałem komunikacji z service workerem
     * @private
     */
    private messagePortDefer: Deferred<MessagePort>;
    /**
     * Zwraca id instancji booking engine, gdy tylko go dostanie, używane przez główne komponenty, które jeszcze nie miały przydzielonego id instancji
     */
    public serviceInstancePromise: Promise<number>;
    private serviceInstanceDefer: Deferred<number>;
    public serviceInstanceId?: number;
    /**
     * Ten promise informuje, gdy komunikacja z service workerem jest już w pełni gotowa
     */
    public communicationReadyPromise: Promise<void>;
    public communicationReady = false;
    private communicationReadyDefer: Deferred<void>;
    public readonly widgetId: number;
    /**
     * Tutaj są przechowywane kanały przez które przechodzą konkretne rodzaje wiadomości z service workera
     * @private
     */
    private messageSubjects: { [messageType: number]:Subject<NfMessage> };
    private componentType: ComponentTypes;
    private static browserTabId?: string;
    private static swIframe: HTMLIFrameElement;
    private iframeReady = false;
    private static swIframeWindow: Window;
    private componentId?: number;
    private requestedGlobalMessageTypes: NfMessageType[];
    /**
     * UUid komponentu, używane zanim komponent dostanie id z service workera
     * @private
     */
    private uuid: number;
    private static swIframeOrigin: string;
    private componentName: string;
    /**
     * Śledzimy jakie wiadomości globalne już wcześniej otrzymaliśmy, żeby nie przetwarzać ich ponownie
     * @private
     */
    private globalMessageVersionsReceived: { [messageType: number]: number } = {};
    public constructor(widgetId: number, componentType: ComponentTypes, componentName: string, requestedGlobalMessageTypes?: NfMessageType[], serviceInstanceId?: number, browserTabId?: string) {
        this.componentType = componentType;
        this.componentName = componentName;
        this.requestedGlobalMessageTypes = requestedGlobalMessageTypes || [];
        if(browserTabId && browserTabId != '') {
            NfServiceWorkerCommunication.browserTabId = browserTabId;
        }
        this.widgetId = widgetId;
        this.serviceInstanceId = serviceInstanceId ?? undefined;
        this.messageSubjects = {};
        this.messagePortDefer = new Deferred<MessagePort>();
        this.serviceInstanceDefer = new Deferred<number>();
        this.serviceInstancePromise = this.serviceInstanceDefer.promise;
        this.communicationReadyDefer = new Deferred();
        this.communicationReadyPromise = this.communicationReadyDefer.promise;
        this.uuid = (new Date()).getTime()+Math.random();

        if(typeof(serviceInstanceId) === 'number') {
            this.serviceInstanceDefer.resolve(serviceInstanceId);
        }

        if(!NfServiceWorkerCommunication.swIframe) {
            this.createSwIframe();
        }
        this.onEventMessage = this.onEventMessage.bind(this);
        window.addEventListener('message', this.onEventMessage);
        if(NfServiceWorkerCommunication.swIframe.getAttribute('ready')) {
            this.onIframeReady();
        }
    }
    private logDebug(...text: any[]) {
        if(window.nfShowDebugMessages) {
            console.debug(`${this.componentName} (${this.componentId})`,  ...text);
        }
    }
    private onEventMessage(event:MessageEvent<NfMessage>) {
        if (event.source != NfServiceWorkerCommunication.swIframeWindow) {
            console.debug('Message from unknown source', event.data);
            return;
        }
        const message = NfMessage.createFromEvent(event);
        if (!message) {
            console.error('Unknown message', event.data);
            return;
        }
        if (message instanceof ServiceWorkerActivated) {
            if (!this.iframeReady) {
                this.logDebug('Message from service worker',event.data);
                this.logDebug('service worker ready');
                this.onIframeReady();
                return;
            }
        } else if (message instanceof ServiceWorkerReloading) {
            this.logDebug('Message from service worker',event.data);
            this.logDebug('service worker reloading');
            this.onSWReload();
            return;
        }
        if (message instanceof RequestComponentRefresh) {
            this.logDebug('Message from service worker', event.data);
            this.sendComponentReconnect();
        } else if (message instanceof NfComponentMessage) {
            if ((this.componentId != message.componentId && this.uuid != message.uuid)
                || this.widgetId != message.widgetId
                || this.serviceInstanceId != message.serviceInstanceId
                || NfServiceWorkerCommunication.browserTabId != message.browserTabId) {
                console.debug('Message rejected', message);
                return;
            }
        }
        this.onMessageReceived(message);
    }
    private onSWReload() {
        this.iframeReady = false;
        NfServiceWorkerCommunication.swIframe.removeAttribute('ready');
        this.communicationReady = false;
        this.communicationReadyDefer = new Deferred();
        this.communicationReadyPromise = this.communicationReadyDefer.promise;

    }
    private onIframeReady() {
        this.iframeReady = true;
        NfServiceWorkerCommunication.swIframe.setAttribute('ready','true');
        const serviceInstanceId = this.serviceInstanceId ?? -1;
        if(serviceInstanceId == -1) {
            this.sendServiceInstanceActivationMessage();
        } else if(!NfServiceWorkerCommunication.browserTabId) {
            this.sendBrowserTabIdRequest();
        } else {
            this.sendComponentActivationMessage(serviceInstanceId);
        }
    }
    private createSwIframe() {
        if(typeof(window[iframeElementName]) == 'object' && window[iframeElementName].constructor.name == 'Window') {
            NfServiceWorkerCommunication.swIframe = document.getElementsByName(iframeElementName)[0] as HTMLIFrameElement;
            NfServiceWorkerCommunication.swIframeWindow = window.frames[iframeElementName];
        } else {

            const iframeElement = document.createElement('iframe');
            // @ts-ignore
            iframeElement.src = import.meta.env.VITE_NF_BASE_URL + 'dist/sw.html';
            iframeElement.name = iframeElementName;
            iframeElement.style.display = 'none';
            document.body.appendChild(iframeElement);
            NfServiceWorkerCommunication.swIframe = iframeElement;
            NfServiceWorkerCommunication.swIframeWindow = window.frames[iframeElementName];
            let origin  = null;
            try {
                // @ts-ignore
                origin = (new URL(import.meta.env.VITE_NF_BASE_URL)).origin;
            } catch(ignore) {
                origin = window.location.origin;
            }
            NfServiceWorkerCommunication.swIframeOrigin = origin;
        }
    }
    private sendBrowserTabIdRequest() {

        if(!NfServiceWorkerCommunication.swIframeWindow ) {
            throw Error('Iframe window not ready');
        }
        const getBrowserTabIdMessage = new GetBrowserTabId( );
        this.logDebug('NfServiceWorkerCommunication: posting GetBrowserTabId', getBrowserTabIdMessage);
        this.logDebug(`Sending message ${getBrowserTabIdMessage.type} to service worker`, this.componentId, this.componentName, getBrowserTabIdMessage);
        NfServiceWorkerCommunication.swIframeWindow.postMessage(getBrowserTabIdMessage,NfServiceWorkerCommunication.swIframeOrigin);
        this.getNextMessagePromise(NfMessageType.SetBrowserTabId).then(
            (result) => {
                this.sendComponentActivationMessage(this.serviceInstanceId as number);
            }
        )
    }
    private sendServiceInstanceActivationMessage() {
        const activationMessage = new ActivateNewBookingEngine( this.widgetId, this.uuid);
        this.logDebug(`Sending message ${activationMessage.type} to service worker`, this.componentId, this.componentName, activationMessage);

        NfServiceWorkerCommunication.swIframeWindow.postMessage(activationMessage,NfServiceWorkerCommunication.swIframeOrigin);
        Promise.all([this.getNextMessagePromise(NfMessageType.SetServiceInstanceId), this.getNextMessagePromise(NfMessageType.SetBrowserTabId)]).then(
            (result) => {
                this.sendComponentActivationMessage(this.serviceInstanceId as number);
            }
        )
    }

    private sendComponentActivationMessage(serviceInstanceId: number) {
        if(!isNumber(serviceInstanceId)) {
            throw Error('Service worker did not provide service instance id ');
        }
        const activationMessage = new ActivateNewBookingEngineComponent(serviceInstanceId, this.widgetId,
            NfServiceWorkerCommunication.browserTabId, this.componentType, this.requestedGlobalMessageTypes,
            this.uuid);
        //this.logDebug('NfServiceWorkerCommunication: posting ActivateNewBookingEngineComponent', activationMessage);

        this.logDebug(`Sending message ${activationMessage.type} to service worker`, this.componentId, this.componentName, activationMessage);
        NfServiceWorkerCommunication.swIframeWindow.postMessage(activationMessage, NfServiceWorkerCommunication.swIframeOrigin);
    }
    private onMessagePortError(baseMessage: string, event: MessageEvent<any>) {
        console.error(baseMessage, event);
    }

    private onMessageReceived(message: NfMessage) {
        if(message instanceof SetBrowserTabId) {
            this.logDebug('Message from service worker',message);
            //this.logDebug('NfServiceWorkerCommunication: received message', message);
            NfServiceWorkerCommunication.browserTabId = message.browserTabId;
        } else if(message instanceof SetServiceInstanceId) {
            if(message.uuid != this.uuid) {
                return;
            }
            this.logDebug('Message from service worker',message);
            this.serviceInstanceId = +message.serviceInstanceId;
            //this.logDebug('NfServiceWorkerCommunication: received message', message);
            this.serviceInstanceDefer.resolve(this.serviceInstanceId);
        } else if(message instanceof SetComponentId) {
            if(message.uuid != this.uuid) {
                return;
            }
            this.logDebug('Message from service worker',message);
            this.componentId = message.componentId;
            //this.logDebug('NfServiceWorkerCommunication: received message', message);
        }
        if(NfGlobalMessage.isSuperClassOf(message)) {
            if (null !== message.serviceInstanceId && message.serviceInstanceId !== this.serviceInstanceId) {
                return;
            }
            if(get(this,['globalMessageVersionsReceived',message.type],0) < message.globalMessageVersion) {
                this.globalMessageVersionsReceived[message.type] = message.globalMessageVersion;
            } else {
                return;
            }
        }
        if(this.messageSubjects[message.type]) {
            //this.logDebug('NfServiceWorkerCommunication: received message', message);
            this.logDebug('Message from service worker',message);
            this.messageSubjects[message.type].next(message);
        }
        if(this.communicationReadyDefer.isPending() && !isUndefined(NfServiceWorkerCommunication.browserTabId)
            && !isUndefined(this.componentId) && !isUndefined(this.serviceInstanceId)) {
            //this.logDebug('Component communication ready', NfServiceWorkerCommunication.browserTabId, this.serviceInstanceId, this.componentId);
            this.communicationReadyDefer.resolve();
            this.communicationReady = true;
        }
    }
    public getComponentId() {
        return this.componentId as number;
    }
    public getBrowserTabId() {
        return NfServiceWorkerCommunication.browserTabId as string;
    }
    public getNextMessagePromise(messageType: NfMessageType): Promise<NfMessage> {
        if(!this.messageSubjects[messageType]) {
            this.messageSubjects[messageType] = new Subject<NfMessage>();
        }
        return lastValueFrom(this.messageSubjects[messageType].pipe(first()))
            .catch(showPromiseError('While waiting for message ' + messageType));
    }

    /**
     * Podaje observable który przyniesie wiadomości z service workera podanego typu
     * Subskrypcja będzie automatycznie anulowana na koniec życia komponentu
     * @param messageType
     */
    public getMessageObservable(messageType: NfMessageType): Observable<NfMessage> {
        if(!this.messageSubjects[messageType]) {
            this.messageSubjects[messageType] = new Subject<NfMessage>();
        }
        return this.messageSubjects[messageType];
    }
    public sendMessage(message: NfMessage): Promise<void> {
        if(this.communicationReady) {
            this.processMessage(message);
            this.logDebug(`Sending message ${message.type} to service worker`, this.componentId, this.componentName, message);

            //this.logDebug(`Sending message ${message.type} to service worker`, message);
            NfServiceWorkerCommunication.swIframeWindow.postMessage(message,NfServiceWorkerCommunication.swIframeOrigin);
            return Promise.resolve();
        } else {
            return this.communicationReadyDefer.promise.then( () => {
                    this.processMessage(message);
                    this.logDebug(`Sending message ${message.type} to service worker`, this.componentId, this.componentName, message);

                    //this.logDebug(`Sending message ${message.type} to service worker`, message);
                    NfServiceWorkerCommunication.swIframeWindow.postMessage(message,NfServiceWorkerCommunication.swIframeOrigin);
                }
            ).catch(showPromiseError('While sending message', message));
        }
    }

    private processMessage(message: NfMessage | NfComponentMessage | NfComponentGlobalMessage) {
        if (message instanceof NfComponentMessage || NfComponentGlobalMessage.isSuperClassOf(message)) {
            message.serviceInstanceId = this.serviceInstanceId as number;
            message.browserTabId = NfServiceWorkerCommunication.browserTabId as string;
            message.componentId = this.componentId as number;
            message.widgetId = this.widgetId as number;
            message.uuid = this.uuid as number;
        }
    }

    private sendComponentReconnect() {
        if(typeof( this.componentId) != 'undefined') {
            const message = new ComponentRefresh(this.serviceInstanceId as number,
                this.widgetId, NfServiceWorkerCommunication.browserTabId, this.componentType, this.requestedGlobalMessageTypes,
                this.uuid, this.componentId);
            this.logDebug(`Sending message ${message.type} to service worker`, this.componentId, this.componentName, message);
            NfServiceWorkerCommunication.swIframeWindow.postMessage(message, NfServiceWorkerCommunication.swIframeOrigin);
        }
    }

    /**
     * Uruchamiane automatycznie gdy komponent jest wyłączany
     */
    public onRemoving() {
        for(let subject  in this.messageSubjects) {
            this.messageSubjects[subject].unsubscribe();
        }
        this.messageSubjects = {};
        window.removeEventListener('message',this.onEventMessage);
        if(!isUndefined(this.getComponentId()) && !isUndefined(this.getBrowserTabId())) {
            this.sendMessage(new ComponentRemoved(this.getComponentId(), this.getBrowserTabId()));
        }
    }

}